import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  SortingState
} from "@tanstack/react-table";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportDropdownButton from "Common/Components/Buttons/ExportDropdownButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import { usePersistedPageIndex } from "Common/Hooks/UsePaginationHooks";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import {
  useEffect,
  useState
} from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportCompanyCSV,
  exportCompanyTallyCSV,
  fetchCompanies as onFetchCompanies,
  updateStatusInCompany as onUpdateStatusInCompany
} from "slices/Masters/company/thunk";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";

const Company = () => {
  document.title = "Customer List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

    const tableState = usePreservedTableState({
      key: TableKeys.MASTER.CUSTOMER,
      defaults: {
        pageIndex: 0,
        pageSize: 10,
        search: "",
        searchInput: "",
        sorting: [],
        columnFilters: [],
        filterValue: [],
      },
    });
  
    const {
      pageIndex,
      pageSize,
      search,
      searchInput,
      sorting,
      columnFilters,
      filterValue,
      setPageIndex,
      setPageSize,
      setSearch,
      setSearchInput,
      setSorting,
      setColumnFilters,
      setFilterValue,
      resetState,
    } = tableState;

  // Redux selector for company state
  const selectCompanies = createSelector(
    (state: any) => state.Company,
    (company) => ({
      companyList: company.companyList,
      rowCount: company.rowCount,
      loading: company.loading,
      error: company.error,
    })
  );

  const { companyList, rowCount, loading, error } =
    useSelector(selectCompanies);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.customer
  );

  // const [pageIndex, setPageIndex] = usePersistedPageIndex(
  //   "companyPageIndex",
  //   0
  // );
  // const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  // const [totalRecords, setTotalRecords] = useState(rowCount);

  // State for edit functionality
  const [editCompany, setEditCompany] = useState<boolean>(false);
  // const [columnFilters, setColumnFilters] = useState<any>();
  const [edit, setEdit] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  useEffect(() => {
    dispatch(
      onFetchCompanies({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);


  useEffect(() => {
    setData(companyList || []);
  }, [companyList]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const handleStatusToggle = async (data: any) => {
    try {
      const updatedStatus = !data.is_active;
      const updatedCompany = {
        id: data.company_id,
        is_active: updatedStatus,
      };
      await dispatch(onUpdateStatusInCompany(updatedCompany))
        .unwrap()
        .then(() => {
          toast.success(
            `Customer ${
              updatedStatus ? "activated" : "deactivated"
            } successfully`
          );
        });
      dispatch(
        onFetchCompanies({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to Update Status");
    }
  };

  const columns = [
    {
      header: "Sr. No.",
      accessorKey: "sr_no",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Company",
      accessorKey: "company_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.CUSTOMER?.FILTER_DROPDOWN(
        "company_name"
      )}`,
    },
    {
      header: "Location Name",
      accessorKey: "branch_name",
      enableColumnFilter: false,
      enableSorting: false,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.CUSTOMER?.FILTER_DROPDOWN(
        "branch_name"
      )}`,
    },
    {
      header: "Industry",
      accessorKey: "industry",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.CUSTOMER?.FILTER_DROPDOWN(
        "industry"
      )}`,
    },
 
    {
      header: "Company Email",
      accessorKey: "company_email",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.CUSTOMER?.FILTER_DROPDOWN(
        "company_email"
      )}`,
    },
    {
      header: "Phone No.",
      accessorKey: "phone_number",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.CUSTOMER?.FILTER_DROPDOWN(
        "phone_number"
      )}`,
    },
    {
      header: "GSTIN",
      accessorKey: "gstin",
      enableColumnFilter: false,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.CUSTOMER?.FILTER_DROPDOWN(
        "gstin"
      )}`,
    },
    {
      header: "GST Type",
      accessorKey: "gst_type",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.CUSTOMER?.FILTER_DROPDOWN(
        "gst_type"
      )}`,
    },
    {
      header: "Vendor Code",
      accessorKey: "vendor_code",
      enableColumnFilter: true,
      enableSorting: false,
      filterType: "dropdown" as const,
      filterFetchUrl: `${API_ENDPOINT?.MASTERS?.CUSTOMER?.FILTER_DROPDOWN(
        "vendor_code"
      )}`,
    },
    {
      header: "Status",
      accessorKey: "is_active",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const pkg = row.original;
        return (
          <div
            style={{ fontSize: "1.5rem", cursor: "pointer" }}
            onClick={() => handleStatusToggle(pkg)}
          >
            <i
              className={`bi ${
                pkg.is_active
                  ? "bi-toggle-on text-success"
                  : "bi-toggle-off text-danger"
              }`}
            />
          </div>
        );
      },
    },
    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => (
        <NameWithDate
          name={row.original.created_by_user?.username}
          date={row.original.created_at}
        />
      ),
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => (
        <NameWithDate
          name={row.original.updated_by_user?.username}
          date={row.original.updated_at}
        />
      ),
    },
    // Filter menu items to check if any will be visible
    // This ensures we only show the Action column if there are visible menu items
    ...(canUpdate || canView
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              const menuItems = [
                {
                  icon: EditIcon,
                  label: "Edit",
                  permission: canUpdate,
                  link: `/master/edit-company/${row.original.company_id}`,
                  color: "primary",
                },
                {
                  icon: VisibilityIcon,
                  label: "View",
                  permission: canView,
                  link: `/master/company/view/${row.original.company_id}`,
                  color: "info",
                },
              ].filter((item) => item.permission !== false);
              return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
            },
          },
        ]
      : []),
    // : []),
  ];

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Customer</h6>
                </Col>
                <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                  {/* Search Box */}
                  <SearchBox
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search..."
                  />

                  {/* Column Visibility + Actions */}
                  <Col xs={12} md={12} lg={8} xl={6} className="">
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                      {/* Left side: Column Visibility */}
                      <div className="">
                        {tableInstance && (
                          <ColumnVisibilityDropdown
                            columns={tableInstance.getAllLeafColumns()}
                            excludedColumns={["action", "sr_no"]}
                          />
                        )}
                      </div>

                      <div className="hstack gap-2">
                        {canView && (
                          <ExportDropdownButton
                            options={[
                              {
                                label: "Export CSV",
                                onExport: async () =>
                                  exportCompanyCSV({
                                    search,
                                    filters: filterValue,
                                  }),
                              },
                              {
                                label: "Export for Tally",
                                onExport: async () =>
                                  exportCompanyTallyCSV({
                                    search,
                                    filters: filterValue,
                                  }),
                              },
                            ]}
                          />
                        )}
                      </div>
                      <div>
                        {canCreate && (
                          <ActionButton
                            label="Add Customer"
                            icon="bi bi-plus-circle"
                            navigateLink="/master/add-company"
                            variant="primary"
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <MasterTableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  manualSorting={true}
                  colValue={setFilterValue}
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Company;
